/**
 * What a thing is, in words, derived from the data that defines it.
 *
 * A player who has never touched this genre is asked to choose between sixteen
 * buildings and units on their first minute, with nothing to go on but a name
 * and a price. Naming a unit "Prism Tank" tells them nothing about whether it
 * beats infantry.
 *
 * **Everything here is computed, never authored.** The counters come out of the
 * armour matrix, the reach and rate come out of the weapon record, and what a
 * building unlocks comes out of the prerequisite graph. Hand-written blurbs
 * would be quicker and would start lying the first time somebody retunes a
 * warhead — and a tooltip that lies about a counter is worse than no tooltip,
 * because the player believes it and loses the fight.
 */

import { ARMOR_NAMES, multiplierFor } from '../sim/data/armor.js'
import { ENTITY_DEFS, ROLE, TAB, defById, type EntityDef } from '../sim/data/entities.js'
import { NO_WEAPON, weaponById } from '../sim/data/weapons.js'
import * as fix from '../core/fixed.js'
import { TICK_HZ } from '../core/config.js'

export interface Stat {
  readonly label: string
  readonly value: string
}

export interface Description {
  readonly name: string
  readonly summary: string
  readonly stats: readonly Stat[]
  /** Armour classes this is unusually good against. */
  readonly strong: readonly string[]
  /** Armour classes it struggles with. */
  readonly weak: readonly string[]
  /** What building this unlocks, once it is standing. */
  readonly unlocks: readonly string[]
  /** Structures still missing before this can be built. */
  readonly needs: readonly string[]
}

/** A multiplier this far from parity is worth calling out. */
const STRONG = 1.15
const WEAK = 0.85

const ROLE_SUMMARY: Record<string, string> = {
  [ROLE.BaseRoot]: 'The heart of the base. Losing every one of these loses the match.',
  [ROLE.Power]: 'Supplies power. Without enough, defences go offline and building slows.',
  [ROLE.Refinery]: 'Harvesters unload here. This is where credits actually come from.',
  [ROLE.InfantryProduction]: 'Trains infantry.',
  [ROLE.VehicleProduction]: 'Builds vehicles.',
  [ROLE.TechLab]: 'Unlocks later technology, and surveys the map.',
  [ROLE.AdvancedLab]: 'Unlocks the heaviest units in the game.',
  [ROLE.Airbase]: 'Builds aircraft, and is where they return to rearm.',
  [ROLE.Defense]: 'Static defence. Fires on its own, but only where it is pointed.',
  [ROLE.Superweapon]: 'Charges over time, then fires once for enormous damage.',
  [ROLE.Founder]: 'Deploys into a new base. Press D once it is somewhere you want to build.',
  [ROLE.Harvester]: 'Gathers regolith and carries it back to a refinery. Unarmed.',
  [ROLE.AntiInfantry]: 'Cheap infantry. Good in numbers, poor against armour.',
  [ROLE.Support]: 'Captures enemy buildings rather than destroying them.',
  [ROLE.Scout]: 'Fast and fragile. For finding things, not fighting them.',
  [ROLE.MainBattle]: 'The backbone of an army. Good against most things, best against none.',
  [ROLE.Air]: 'Flies over everything. Carries a few rounds, then must return to rearm.',
  [ROLE.Beam]: 'Long-ranged and slow-firing. Cuts armour; scatters off infantry.',
  [ROLE.Artillery]: 'Outranges every defence, and cannot fire close. Needs an escort.',
  [ROLE.Heavy]: 'Slow and heavily armoured. Holds ground a lighter unit cannot.',
  [ROLE.Transport]:
    'Carries one infantryman and fights with their weapon. Right-click it with infantry selected to load, D to unload.',
  [ROLE.Marksman]:
    'Outranges everything, and only kills people. Press D to dig in where it can see.',
}

/** Everything the sidebar needs to explain one entity. */
export function describe(def: EntityDef, owned: (kind: number) => boolean): Description {
  const stats: Stat[] = [{ label: 'Cost', value: String(def.cost) }]
  stats.push({ label: 'Health', value: String(def.hp) })
  stats.push({ label: 'Armour', value: ARMOR_NAMES[def.armor] ?? '—' })

  const strong: string[] = []
  const weak: string[] = []
  const weapon = def.weapon === NO_WEAPON ? undefined : weaponById(def.weapon)
  if (weapon) {
    stats.push({ label: 'Damage', value: String(weapon.damage) })
    stats.push({ label: 'Range', value: `${fix.toFloat(weapon.range).toFixed(1)} cells` })
    // Shots per second reads better than a cooldown in ticks, which is an
    // implementation detail the player has no way to interpret.
    const rate = weapon.cooldown > 0 ? TICK_HZ / weapon.cooldown : 0
    if (rate > 0) stats.push({ label: 'Rate', value: `${rate.toFixed(1)}/s` })
    if (def.minRange !== undefined) {
      stats.push({ label: 'Min range', value: `${fix.toFloat(def.minRange).toFixed(1)} cells` })
    }

    for (let armor = 0; armor < ARMOR_NAMES.length; armor++) {
      const mult = fix.toFloat(multiplierFor(weapon.warhead, armor))
      if (mult >= STRONG) strong.push(ARMOR_NAMES[armor])
      else if (mult <= WEAK) weak.push(ARMOR_NAMES[armor])
    }
  }

  if (!def.isStructure && def.speed > 0) {
    // Relative to the faction's main battle tank, because "0.048 cells a tick"
    // means nothing and "faster than a tank" means everything.
    const main = ENTITY_DEFS.find((d) => d.faction === def.faction && d.role === ROLE.MainBattle)
    if (main && main.speed > 0) {
      const ratio = def.speed / main.speed
      const word = ratio > 1.25 ? 'fast' : ratio < 0.8 ? 'slow' : 'average'
      stats.push({ label: 'Speed', value: word })
    }
  }
  if (def.power < 0) stats.push({ label: 'Power', value: String(def.power) })
  else if (def.power > 0) stats.push({ label: 'Power', value: `+${def.power}` })

  return {
    name: def.name,
    summary: ROLE_SUMMARY[def.role] ?? '',
    stats,
    strong,
    weak,
    unlocks: unlockedBy(def),
    needs: missingPrereqs(def, owned),
  }
}

/** Names of everything that lists `def` as a prerequisite. */
function unlockedBy(def: EntityDef): string[] {
  const out: string[] = []
  for (const other of ENTITY_DEFS) {
    if (other.faction !== def.faction) continue
    if (!other.prereqs.includes(def.id)) continue
    out.push(other.name)
  }
  return out
}

/**
 * Structures still missing, walked to the root of the chain.
 *
 * Listing only the immediate prerequisite is unhelpful when the player is three
 * buildings away: "needs a Prism Works" is no use to somebody who does not have
 * the Comms Mast the Works itself requires.
 */
function missingPrereqs(def: EntityDef, owned: (kind: number) => boolean): string[] {
  const out: string[] = []
  const walk = (target: EntityDef): void => {
    for (const id of target.prereqs) {
      if (owned(id)) continue
      const prereq = defById(id)
      if (!prereq) continue
      walk(prereq)
      if (!out.includes(prereq.name)) out.push(prereq.name)
    }
  }
  walk(def)
  // The prerequisite graph says what unlocks a thing, not where it is built.
  // A harvester with its refinery standing is still unbuildable without a
  // Foundry, and a tooltip that omits the factory explains a greyed icon with
  // a list of requirements the player has already met.
  const producer = producerDefFor(def)
  if (producer && !owned(producer.id)) {
    walk(producer)
    if (!out.includes(producer.name)) out.push(producer.name)
  }
  return out
}

/** The structure that produces this definition's tab, mirroring `Build.producerFor`. */
function producerDefFor(def: EntityDef): EntityDef | undefined {
  const find = (tab: number): EntityDef | undefined =>
    ENTITY_DEFS.find((d) => d.faction === def.faction && d.producesTab === tab)
  return find(def.tab) ?? (def.tab === TAB.Defense ? find(TAB.Structure) : undefined)
}
